import { IonIcon } from "@ionic/react";
import { warningOutline } from "ionicons/icons";
import React from "react";
import Clock from "../../../image/icons/map/details/ClockIcon.svg?react";
import Info from "../../../image/icons/map/details/Info.svg?react";
import { getUIColorPalette } from "../../../theme/ColorPalette";
import { capitalizeFirstLetter } from "../../../tools/StringTools";
import { formatTime } from "../../../tools/TimeRelated";
import { getItineraryLegColorHex } from "../../../tools/TransportModeColorsTools";
import { getTransportModeGroupForItineraryLeg, TransportModeGroup } from "../../../tools/TransportModeGroupsTools";
import { type ItineraryLegDTO } from "../../../types/dtos/api/itinerary/itinerary.dto";
import {
    isOdmItineraryLegMode,
    ODM_MOBILITY_NOTICE_COLOR,
    ODM_MOBILITY_NOTICE_MESSAGE,
    RENTAL_ORIGIN_STATION_RETURN_WARNING_COLOR,
    RENTAL_ORIGIN_STATION_RETURN_WARNING_MESSAGE,
    shouldShowRentalOriginStationReturnWarning,
} from "./itineraryDetails.tools";

interface ItineraryDetailsLegDetailsProps {
    "leg":      ItineraryLegDTO;
    "isDarkUi": boolean;
}

const getRentalVehicleTypeLabel = ( vehicleType?: string ): string => {
    switch ( vehicleType ) {
        case "BICYCLE":
            return "Vélo";

        case "CARGO_BICYCLE":
            return "Vélo cargo";

        case "CAR":
            return "Voiture";

        case "MOPED":
            return "Cyclomoteur";

        case "SCOOTER_STANDING":
            return "Trottinette";

        case "SCOOTER_SEATED":
            return "Scooter";

        case "OTHER":
            return "Véhicule partagé";

        default:
            return "";
    }
};

const getRentalVehiclePropulsionLabel = ( propulsion?: string ): string => {
    switch ( propulsion ) {
        case "HUMAN":
            return "classique";

        case "ELECTRIC_ASSIST":
            return "à assistance électrique";

        case "ELECTRIC":
            return "électrique";

        case "COMBUSTION":
            return "thermique";

        case "COMBUSTION_DIESEL":
            return "diesel";

        case "HYBRID":
            return "hybride";

        case "PLUG_IN_HYBRID":
            return "hybride rechargeable";

        case "HYDROGEN_FUEL_CELL":
            return "à hydrogène";

        default:
            return "";
    }
};

const formatRentalVehicleDetails = ( vehicleType?: string, propulsion?: string ): string => {
    const typeLabel = getRentalVehicleTypeLabel( vehicleType );
    const propulsionLabel = getRentalVehiclePropulsionLabel( propulsion );

    if ( typeLabel.length === 0 ) {
        return "";
    }

    if ( propulsionLabel.length === 0 ) {
        return typeLabel;
    }

    return `${ typeLabel } ${ propulsionLabel }`;
};

const ItineraryDetailsLegDetails: React.FC<ItineraryDetailsLegDetailsProps> = ( { leg, isDarkUi } ) => {
    const modeGroup = getTransportModeGroupForItineraryLeg( leg );
    const palette = getUIColorPalette( isDarkUi );

    // For transit-like legs, expose both duration and stop count when available.
    const intermediateStopsCount = leg.intermediateStops?.length ?? 0;
    const stopLabel = intermediateStopsCount === 1 ? "arrêt" : "arrêts";
    const durationLabel = intermediateStopsCount > 0
        ? `${ formatTime( leg.duration, true ) } (${ intermediateStopsCount } ${ stopLabel })`
        : formatTime( leg.duration, true );
    let informations = "";

    if ( [ TransportModeGroup.BUS, TransportModeGroup.TRAM, TransportModeGroup.TRAIN ].includes( modeGroup ) ) {
        if ( leg.from?.platform ) {
            informations = `Quai ${ leg.from.platform } · `;
        }
        if ( leg.route?.headsign ) {
            informations += `Direction ${ leg.route.headsign }`;
        }
    }

    if ( [ TransportModeGroup.BLABLACAR, TransportModeGroup.BLABLADAILY ].includes( modeGroup ) && leg.rental ) {
        informations = leg.rental.provider?.name ?? "";

        const rentalVehicle = leg.rental.vehicle;
        if ( modeGroup === TransportModeGroup.BLABLACAR && rentalVehicle && ( rentalVehicle.brand || rentalVehicle.model ) ) {
            informations += ` · ${ capitalizeFirstLetter( rentalVehicle.brand ) } ${ capitalizeFirstLetter( rentalVehicle.model ) }`;
        }
    }

    if ( modeGroup === TransportModeGroup.RENTAL ) {
        informations = leg.rental?.provider?.name ?? "";
        const rentalVehicleDetails = formatRentalVehicleDetails( leg.rental?.vehicle?.type, leg.rental?.vehicle?.propulsion );

        if ( rentalVehicleDetails.length > 0 ) {
            informations = informations.length > 0
                ? `${ informations } · ${ rentalVehicleDetails }`
                : rentalVehicleDetails;
        }
    }

    const informationsElement = informations.length > 0
        ? (
        <span
            className="mb-1 flex max-w-full items-center"
            style={{ "color": palette.overlayTextSecondary }}
        >
                    <Info
                        className="mr-1 inline-block w-3 shrink-0 align-middle h-[12px]"
                        style={{ "color": palette.overlayTextSecondary }}
                    />
                    { informations }
        </span>
        )
        : null;

    const rentalOriginReturnWarningElement = shouldShowRentalOriginStationReturnWarning( leg )
        ? (
            <span
                className="mb-1 flex max-w-full items-start gap-1 leading-snug"
                style={{ "color": RENTAL_ORIGIN_STATION_RETURN_WARNING_COLOR }}
            >
                <IonIcon
                    aria-hidden={true}
                    className="mt-px shrink-0 text-[14px]"
                    icon={warningOutline}
                    style={{ "color": RENTAL_ORIGIN_STATION_RETURN_WARNING_COLOR }}
                />
                <span>
                    {RENTAL_ORIGIN_STATION_RETURN_WARNING_MESSAGE}
                </span>
            </span>
        )
        : null;

    const odmMobilityNoticeElement = isOdmItineraryLegMode( leg.mode )
        ? (
            <span
                className="mb-1 flex max-w-full items-start gap-1 leading-snug"
                style={{ "color": ODM_MOBILITY_NOTICE_COLOR }}
            >
                <IonIcon
                    aria-hidden={true}
                    className="mt-px shrink-0 text-[14px]"
                    icon={warningOutline}
                    style={{ "color": ODM_MOBILITY_NOTICE_COLOR }}
                />
                <span>
                    {ODM_MOBILITY_NOTICE_MESSAGE}
                </span>
            </span>
        )
        : null;

    return (
        <div
            className="flex w-full items-stretch"
            id={`itinerary-details-leg-${ leg.id }-details`}
        >
            <div className="flex items-stretch justify-center pl-[36px] pr-2">
                <span
                    className="mt-[-15px] block w-[3px] self-stretch"
                    style={{ "backgroundColor": getItineraryLegColorHex( leg ) }}
                />
            </div>
            <div className="w-full text-xs pb-2">
                {/* Informations */}
                { informationsElement }
                {/* Duration */}
                <span
                    className="mb-1 flex max-w-full items-center "
                    style={{ "color": palette.overlayTextSecondary }}
                >
                    <Clock
                        className="mr-1 inline-block w-3 shrink-0 align-middle"
                        style={{ "color": palette.overlayTextSecondary }}
                    />
                    {`Durée du trajet : ${ durationLabel }`}
                </span>
                {odmMobilityNoticeElement}
                {rentalOriginReturnWarningElement}
            </div>
        </div>
    );
};

export default ItineraryDetailsLegDetails;
